import { inject, Injectable, signal } from '@angular/core';
import { UtilsService, CoreService } from 'src/app/common';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { DigitalBusinessCard, CardPermission, OrganizationMember } from '../models/business-card.model';

// --- Service ---
@Injectable({
  providedIn: 'root'
})
export class OrganizationBusinessCardsService {
    // Signals/state
    private _cards = signal<DigitalBusinessCard[]>([]);
    public cards = this._cards.asReadonly();

    private _cardPermissions = signal<CardPermission[]>([]);
    public cardPermissions = this._cardPermissions.asReadonly();

    private _isCardPermissionsLoading = signal<boolean>(false);
    public isCardPermissionsLoading = this._isCardPermissionsLoading.asReadonly();

    private core = inject(CoreService<{}>);
    private utils = inject(UtilsService);
    private cognitiveBaseUrl = environment.cognitiveServiceUrl;
    private identityBaseUrl = environment.identityUrl; // change to your projects API base if different

    /**
     * Loads all business cards for an org, updating the signal.
     */
    public async loadCardsByOrgIdIfNeeded(orgId: number | string, force = false): Promise<boolean> {
        if (this._cards().length > 0 && !force) {
            return true;
        }

        const cards = await this.fetchCardsByOrgId(orgId);
        if (cards === null) {
            return false;
        }

        this._cards.set(cards);
        return true;
    }

    /**
     * Fetches business cards for an organization.
     */
    public async fetchCardsByOrgId(orgId: number | string): Promise<DigitalBusinessCard[] | null> {
        this.utils.logger.info('fetchCardsByOrgId() Fetching cards for org:', orgId);
        if (!orgId) {
            this.utils.logger.warn('fetchCardsByOrgId() No orgId provided');
            return null;
        }
        try {
            const url = `${this.cognitiveBaseUrl}/DigitalBusinessCard/org/${orgId}`;
            this.utils.logger.info('fetchCardsByOrgId() API URL:', url);
            const data = await firstValueFrom(this.core.get(url));
            this.utils.logger.info('fetchCardsByOrgId() API response:', data);
            return Array.isArray(data) ? data as DigitalBusinessCard[] : [];
        } catch (error) {
            this.utils.logger.error('fetchCardsByOrgId() error:', error);
            return null;
        }
    }

    /**
     * Loads access list (members with access) for a card.
     */
    async loadCardPermissions(cardId: string, force = false): Promise<void> {
        if (this._cardPermissions().length > 0 && !force) return;
        const permissions = await this.fetchCardPermissions(cardId);
        if (permissions){
            this.utils.logger.info('loadCardPermissions() Setting card permissions:', permissions);
            this._cardPermissions.set(permissions);
        }
    }

    /**
     * Fetch members with access to a card.
     */
    async fetchCardPermissions(cardId: string): Promise<CardPermission[] | null> {
        this.utils.logger.info('fetchCardPermissions() for card:', cardId);
        if (!cardId) {
            this.utils.logger.warn('fetchCardPermissions() No cardId provided');
            return null;
        }
        this._isCardPermissionsLoading.set(true);
        try {
            const url = `${this.identityBaseUrl}/CardPermissions/card/${cardId}`;
            this.utils.logger.info('fetchCardPermissions() API URL:', url);
            const data = await firstValueFrom(this.core.get(url));
            return Array.isArray(data) ? data as CardPermission[] : [];
        } catch (error) {
            this.utils.logger.error('fetchCardPermissions() error:', error);
            return null;
        } finally {
            this.utils.logger.info('fetchCardPermissions() completed for card:', cardId);
            this._isCardPermissionsLoading.set(false);
        }
    }

    /**
     * Fetches all card permissions for a user in the current organization.
     */
    public async fetchCardPermissionsByUserId(userId: string): Promise<CardPermission[] | null> {
        this.utils.logger.info('fetchCardPermissionsByUserId() for user:', userId);
        if (!userId) {
            this.utils.logger.warn('fetchCardPermissionsByUserId() No userId provided');
            return null;
        }

        try {
            const encodedUserId = encodeURIComponent(userId);
            const url = `${this.identityBaseUrl}/CardPermissions/user/${encodedUserId}`;
            const data = await firstValueFrom(this.core.get(url));
            return Array.isArray(data) ? data as CardPermission[] : [];
        } catch (error) {
            this.utils.logger.error('fetchCardPermissionsByUserId() error:', error);
            return null;
        }
    }

    /**
     * Adds a single card permission (grants user access to card).
     */
    async addCardPermission(permission: {
        cardId: string;
        userId: string;
        role: string;
        orgId: number;
    }): Promise<CardPermission | null> {
        this.utils.logger.info('addCardPermission() Adding user to card', permission);

        try {
            const url = `${this.identityBaseUrl}/CardPermissions`;
            this.utils.logger.info('addCardPermission() API URL:', url);
            const data = await firstValueFrom(this.core.post(url, permission));
            return data as CardPermission;
        } catch (error) {
            this.utils.logger.error('addCardPermission() error:', error);
            return null;
        } finally {
            this.utils.logger.info('addCardPermission() completed');
        }
    }

    /**
     * Deletes a card permission by its ID.
     * @param id The project permission ID to delete.
     * @returns Promise<boolean> True if successful, false otherwise.
     */
    async removeCardPermission(id: string): Promise<boolean> {
        if (!id) {
            this.utils.logger.error('removeCardPermission() No ID provided.');
            return false;
        }
        try {
            const url = `${this.identityBaseUrl}/CardPermissions/${id}`;
            this.utils.logger.info('removeCardPermission() API URL:', url);
            await firstValueFrom(this.core.delete(url));
            this.utils.logger.info('removeCardPermission() success');
            return true;
        } catch (error) {
            this.utils.logger.error('removeCardPermission() error:', error);
            return false;
        }
    }

    async deleteCard(cardId: string, projectId: string): Promise<boolean> {
        if (!cardId) {
            this.utils.logger.error('deleteCard() No cardId provided.');
            return false;
        }
        try {
            const url = `${this.cognitiveBaseUrl}/DigitalBusinessCard/${cardId}/project/${projectId}`;
            this.utils.logger.info('deleteCard() API URL:', url);
            await firstValueFrom(this.core.delete(url));
            this.utils.logger.info('deleteCard() success');
            this._cards.update(cards => cards.filter(c => c.id !== cardId));
            return true;
        } catch (error) {
            this.utils.logger.error('deleteCard() error:', error);
            return false;
        }
    }

    /**
     * Updates a card permission.
     * @param permission CardPermission object
     * @returns Promise<CardPermission | null>
     */
    async updateCardPermission(permission: CardPermission): Promise<CardPermission | null> {
        if (!permission?.id) {
            this.utils.logger.error('updateCardPermission() No permission id provided.');
            return null;
        }
        try {
            const url = `${this.identityBaseUrl}/CardPermissions/${permission.id}`;
            this.utils.logger.info('updateCardPermission() API URL:', url);
            const data = await firstValueFrom(this.core.bulkPut(url, permission));
            this.utils.logger.info('updateCardPermission() success:', data);
            // No content returned, so we return what we sent as confirmation
            return permission;
        } catch (error) {
            this.utils.logger.error('updateCardPermission() error:', error);
            return null;
        }
    }

}
